import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTimesheets } from '../logic/timesheetProvider';
import { useAuth } from '../../auth/logic/authProvider';
import { TimesheetRepository } from '../data/timesheetRepository';
import { useI18n } from '../../../shared/i18n/I18nContext';
import { useTheme } from '../../../shared/theme/ThemeContext';
import TimesheetDialog from './TimesheetDialog';
import TimesheetFilters from './TimesheetFilters';
import TimesheetList from './TimesheetList';
import TimesheetPeriodControls from './TimesheetPeriodControls';
import TimesheetStats from './TimesheetStats';
import TimesheetTable from './TimesheetTable';
import { useProjects, useTasks } from './useProjectTaskOptions';
import {
  addDays,
  addMonths,
  daysBetween,
  firstOfMonth,
  formatDate,
  lastOfMonth,
  mondayOf,
  toISODate,
} from './timesheetDateUtils';
import { durationParts, formatHours, groupByProject, hoursOf, idOf } from './timesheetUtils';
import './TimesheetsPage.css';

const withCascade = (current, name, value) => ({
  ...current,
  [name]: value,
  ...(name === 'project' ? { task: '' } : {}),
});

const makeEmptyDraft = () => ({
  id: null,
  project: '',
  task: '',
  // en-CA is YYYY-MM-DD in local time; toISOString() would give the UTC day.
  date: new Date().toLocaleDateString('en-CA'),
  hours: '',
  minutes: '',
  description: '',
});

const TimesheetSkeletonBlock = ({ className = '' }) => <div className={`timesheet-skeleton ${className}`} />;

const TimesheetStatsSkeleton = () => (
  <div className="timesheets-stats" aria-hidden="true">
    {[0, 1, 2].map((item) => (
      <div className="timesheets-stat timesheets-stat--skeleton" key={item}>
        <TimesheetSkeletonBlock className="timesheet-skeleton--label" />
        <TimesheetSkeletonBlock className="timesheet-skeleton--stat" />
        <TimesheetSkeletonBlock className="timesheet-skeleton--label" />
      </div>
    ))}
  </div>
);

const TimesheetHeaderActionsSkeleton = () => (
  <div className="timesheets-head-actions" aria-hidden="true">
    <TimesheetSkeletonBlock className="timesheet-skeleton--summary" />
    <TimesheetSkeletonBlock className="timesheet-skeleton--action" />
    <TimesheetSkeletonBlock className="timesheet-skeleton--action" />
  </div>
);

const TimesheetFiltersSkeleton = () => (
  <div className="timesheets-filters timesheets-filters--skeleton" aria-hidden="true">
    <TimesheetSkeletonBlock className="timesheet-skeleton--control" />
    <TimesheetSkeletonBlock className="timesheet-skeleton--control" />
    <TimesheetSkeletonBlock className="timesheet-skeleton--control" />
  </div>
);

const TimesheetPeriodSkeleton = () => (
  <div className="timesheets-week" aria-hidden="true">
    <TimesheetSkeletonBlock className="timesheet-skeleton--button" />
    <TimesheetSkeletonBlock className="timesheet-skeleton--period" />
    <TimesheetSkeletonBlock className="timesheet-skeleton--button" />
  </div>
);

const TimesheetBodySkeleton = () => (
  <TimesheetSkeletonBlock className="timesheet-skeleton--table" />
);

const TimesheetBodyContent = ({
  currentWeekStart,
  error,
  formatDate,
  grouped,
  locale,
  onDelete,
  onEdit,
  onOpenCell,
  tableDays,
  tableTotal,
  theme,
  timesheets,
  today,
  t,
  view,
}) => {
  if (error) {
    return (
      <p className="timesheets-status" style={{ color: theme.error }}>
        {error.message}
      </p>
    );
  }

  if (timesheets.length === 0) {
    return (
      <p className="timesheets-status" style={{ color: theme.secondaryText }}>
        {t('noTimesheetRecordsYet') || 'No timesheet records yet'}
      </p>
    );
  }

  if (view === 'list') {
    return (
      <TimesheetList
        formatDate={formatDate}
        onDelete={onDelete}
        onEdit={onEdit}
        t={t}
        theme={theme}
        timesheets={timesheets}
      />
    );
  }

  return (
    <TimesheetTable
      formatDate={formatDate}
      grouped={grouped}
      locale={locale}
      onOpenCell={onOpenCell}
      t={t}
      tableDays={tableDays}
      tableTotal={tableTotal}
      theme={theme}
      today={today}
      currentWeekStart={currentWeekStart}
    />
  );
};

const TimesheetsPage = ({
  fixedTaskId = '',
  fixedTaskName = '',
  initialTablePeriod = 'week',
  initialView = 'table',
  showFilters = true,
  title = '',
}) => {
  const { t, locale } = useI18n();
  const { theme } = useTheme();
  const { timesheets, listTimesheets, createTimesheet, updateTimesheet, deleteTimesheet, clearError, isLoading, error } =
    useTimesheets();
  const { user } = useAuth();

  const [filters, setFilters] = useState({ project: '', task: fixedTaskName || '', dateFrom: '', dateTo: '' });
  const [view, setView] = useState(initialView);
  const [tablePeriod, setTablePeriod] = useState(initialTablePeriod);
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [monthStart, setMonthStart] = useState(() => firstOfMonth(new Date()));
  const [draft, setDraft] = useState(() => makeEmptyDraft());
  const [stats, setStats] = useState({ today: 0, week: 0, month: 0 });
  const [statsLoading, setStatsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const dialogRef = useRef(null);

  const projects = useProjects(user?.accessToken);
  const filterProjectId = idOf(projects, filters.project);
  const draftProjectId = idOf(projects, draft.project);
  const filterTasks = useTasks(filterProjectId, user?.accessToken);
  const draftTasks = useTasks(draft.id ? '' : draftProjectId, user?.accessToken);
  const filterTaskId = fixedTaskId || idOf(filterTasks, filters.task);
  const draftTaskId = idOf(draftTasks, draft.task);

  const weekDays = useMemo(() => Array.from({ length: 7 }, (unused, day) => addDays(weekStart, day)), [weekStart]);
  const monthEnd = useMemo(() => lastOfMonth(monthStart), [monthStart]);
  const monthDays = useMemo(() => daysBetween(monthStart, monthEnd), [monthStart, monthEnd]);
  const tableDays = tablePeriod === 'month' ? monthDays : weekDays;
  const grouped = useMemo(() => groupByProject(timesheets), [timesheets]);

  const today = toISODate(new Date());
  const currentWeekStart = mondayOf(new Date());
  const isThisWeek = weekStart === currentWeekStart;
  const isThisMonth = monthStart === firstOfMonth(new Date());
  const isCurrentPeriod = tablePeriod === 'month' ? isThisMonth : isThisWeek;
  const columnTotal = (day) => grouped.reduce((sum, group) => sum + (group.byDay[day] || 0), 0);
  const tableTotal = tableDays.reduce((sum, day) => sum + columnTotal(day), 0);
  const totalHours = timesheets.reduce((sum, item) => sum + hoursOf(item), 0);

  const dateFrom = view === 'table' ? tableDays[0] : filters.dateFrom;
  const dateTo = view === 'table' ? tableDays[tableDays.length - 1] : filters.dateTo;
  const formatLocalDate = (value) => formatDate(value, locale);

  useEffect(() => {
    listTimesheets({ projectId: filterProjectId, taskId: filterTaskId, dateFrom, dateTo });
  }, [listTimesheets, filterProjectId, filterTaskId, dateFrom, dateTo]);

  useEffect(() => {
    let cancelled = false;
    const currentMonthStart = firstOfMonth(new Date());
    const currentMonthEnd = lastOfMonth(currentMonthStart);

    const loadStats = async () => {
      setStatsLoading(true);
      try {
        const result = await TimesheetRepository.listTimesheets({
          projectId: filterProjectId,
          taskId: filterTaskId,
          dateFrom: currentMonthStart,
          dateTo: currentMonthEnd,
          accessToken: user?.accessToken,
        });
        if (cancelled) return;
        const nextStats = result.timesheets.reduce(
          (totals, line) => {
            const hours = hoursOf(line);
            if (line.date === today) totals.today += hours;
            if (line.date >= currentWeekStart && line.date <= today) totals.week += hours;
            totals.month += hours;
            return totals;
          },
          { today: 0, week: 0, month: 0 },
        );
        setStats(nextStats);
      } catch {
        if (!cancelled) setStats({ today: 0, week: 0, month: 0 });
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };

    loadStats();
    return () => {
      cancelled = true;
    };
  }, [currentWeekStart, filterProjectId, filterTaskId, timesheets, today, user?.accessToken]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFilters((current) => withCascade(current, name, value));
  };

  const handleDraftChange = (event) => {
    const { name, value } = event.target;
    setDraft((current) => withCascade(current, name, value));
  };

  const openDialog = (timesheet) => {
    clearError();
    setDraft(
      timesheet
        ? {
            id: timesheet.id,
            project: timesheet.projectName ?? '',
            task: timesheet.taskName ?? '',
            date: timesheet.date ?? '',
            description: timesheet.description ?? '',
            ...durationParts(timesheet),
          }
        : makeEmptyDraft(),
    );
    dialogRef.current?.showModal();
  };

  const openCell = (project, task, date) => {
    clearError();
    setDraft({ ...makeEmptyDraft(), project, task, date });
    dialogRef.current?.showModal();
  };

  const closeOnBackdrop = (event) => {
    if (event.target !== event.currentTarget) return;
    const { top, bottom, left, right } = event.currentTarget.getBoundingClientRect();
    const inside =
      event.clientX >= left && event.clientX <= right && event.clientY >= top && event.clientY <= bottom;
    if (!inside) event.currentTarget.close();
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    const pad = (value) => String(Number(value) || 0).padStart(2, '0');
    const timeSpent = `${pad(draft.hours)}:${pad(draft.minutes)}`;
    const saved = draft.id
      ? await updateTimesheet({ id: draft.id, timeSpent, description: draft.description })
      : await createTimesheet({ ...draft, taskId: draftTaskId, timeSpent });
    if (saved) dialogRef.current?.close();
  };

  const handleDelete = (timesheet) => {
    const label = timesheet.description || timesheet.taskName || `#${timesheet.id}`;
    if (window.confirm(`${t('confirmDelete') || 'Confirm Delete'} — ${label}`)) deleteTimesheet(timesheet.id);
  };

  const shiftTablePeriod = (direction) => {
    if (tablePeriod === 'month') {
      setMonthStart((current) => addMonths(current, direction));
      return;
    }
    setWeekStart((current) => addDays(current, direction * 7));
  };

  return (
    <div className="timesheets-card" style={{ borderColor: theme.border }}>
      <div className="timesheets-head">
        <div className="timesheets-title-row">
          <h2 className="timesheets-title">{title || t('timesheets') || 'Timesheets'}</h2>
          <button
            type="button"
            className={`timesheets-toggle${isExpanded ? ' timesheets-toggle--expanded' : ''}`}
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Collapse timesheets' : 'Expand timesheets'}
            aria-controls="timesheets-content"
            onClick={() => setIsExpanded((current) => !current)}
          >
            <span aria-hidden="true">〱</span>
          </button>
        </div>
        <div className="timesheets-head-side">
          {isExpanded &&
            (isLoading ? (
              <TimesheetHeaderActionsSkeleton />
            ) : (
              <div className="timesheets-head-actions">
                {timesheets.length > 0 && (
                  <span className="timesheets-summary" style={{ color: theme.secondaryText }}>
                    {timesheets.length} · {formatHours(totalHours)} {t('hours') || 'Hours'}
                  </span>
                )}
                <button
                  type="button"
                  className="timesheets-cancel"
                  onClick={() => setView((current) => (current === 'list' ? 'table' : 'list'))}
                  style={{ borderColor: theme.border, color: theme.text }}
                >
                  {view === 'list' ? t('tableView') : t('listView')}
                </button>
                <button
                  type="button"
                  className="timesheets-submit"
                  onClick={() => openDialog()}
                  style={{ background: theme.primary }}
                >
                  {t('addTimesheet') || 'Add Timesheet'}
                </button>
              </div>
            ))}
          
        </div>
      </div>

      <div
        id="timesheets-content"
        className={`timesheets-content${isExpanded ? '' : ' timesheets-content--collapsed'}`}
      >
        {statsLoading ? (
          <TimesheetStatsSkeleton />
        ) : (
          <TimesheetStats isLoading={statsLoading} stats={stats} t={t} theme={theme} />
        )}

        <TimesheetDialog
          dialogRef={dialogRef}
          draft={draft}
          draftProjectId={draftProjectId}
          draftTaskId={draftTaskId}
          draftTasks={draftTasks}
          error={error}
          isLoading={isLoading}
          onBackdropClick={closeOnBackdrop}
          onChange={handleDraftChange}
          onSubmit={handleCreate}
          projects={projects}
          t={t}
          theme={theme}
        />

        {showFilters &&
          (isLoading ? (
            <TimesheetFiltersSkeleton />
          ) : (
            <TimesheetFilters
              filterProjectId={filterProjectId}
              filterTasks={filterTasks}
              filters={filters}
              onChange={handleChange}
              projects={projects}
              t={t}
              theme={theme}
              view={view}
            />
          ))}

        {isLoading && view === 'table' && <TimesheetPeriodSkeleton />}

        {!isLoading && view === 'table' && (
          <TimesheetPeriodControls
            formatDate={formatLocalDate}
            isCurrentPeriod={isCurrentPeriod}
            onShift={shiftTablePeriod}
            setTablePeriod={setTablePeriod}
            tableDays={tableDays}
            tablePeriod={tablePeriod}
            t={t}
            theme={theme}
          />
        )}

        {isLoading ? (
          <TimesheetBodySkeleton />
        ) : (
          <TimesheetBodyContent
            currentWeekStart={currentWeekStart}
            error={error}
            formatDate={formatLocalDate}
            grouped={grouped}
            locale={locale}
            onDelete={handleDelete}
            onEdit={openDialog}
            onOpenCell={openCell}
            t={t}
            tableDays={tableDays}
            tableTotal={tableTotal}
            theme={theme}
            timesheets={timesheets}
            today={today}
            view={view}
          />
        )}
      </div>
    </div>
  );
};

export default TimesheetsPage;
